import React from 'react';

interface OrganizationSchemaProps {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
  address?: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  phone?: string;
  email?: string;
}

export const OrganizationSchema: React.FC<OrganizationSchemaProps> = ({
  name = "Quimical",
  url = "https://quimical.com.br",
  logo = "https://quimical.com.br/media/logoespecialhome-new.jpg",
  description = "Fornecedor brasileiro de cal virgem e cal hidratada para aplicações industriais, com atuação nacional.",
  address = {
    streetAddress: "Rua Belo Horizonte, 6 Santo Antônio",
    addressLocality: "Arcos",
    addressRegion: "MG",
    postalCode: "35599-170",
    addressCountry: "BR"
  },
  phone = "+5537998606150",
  email = "contato@quimical.com.br"
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": name,
    "url": url,
    "logo": logo,
    "description": description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": address.streetAddress,
      "addressLocality": address.addressLocality,
      "addressRegion": address.addressRegion,
      "postalCode": address.postalCode,
      "addressCountry": address.addressCountry
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": phone,
      "contactType": "customer service",
      "email": email,
      "availableLanguage": ["Portuguese", "English"]
    },
    "sameAs": [
      "https://www.linkedin.com/company/quimical",
      "https://www.facebook.com/quimical"
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

interface ProductSchemaProps {
  name: string;
  description: string;
  image?: string;
  brand?: string;
  manufacturer?: string;
  category?: string;
}

export const ProductSchema: React.FC<ProductSchemaProps> = ({
  name,
  description,
  image = "https://quimical.com.br/media/CAL-VIRGEM.webp",
  brand = "Quimical",
  manufacturer = "Quimical",
  category = "Industrial Chemical"
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "image": image,
    "brand": {
      "@type": "Brand",
      "name": brand
    },
    "manufacturer": {
      "@type": "Organization",
      "name": manufacturer
    },
    "category": category,
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "BRL"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

interface WebsiteSchemaProps {
  name?: string;
  url?: string;
  description?: string;
  publisher?: string;
}

export const WebsiteSchema: React.FC<WebsiteSchemaProps> = ({
  name = "Quimical - Cal Virgem e Cal Hidratada",
  url = "https://quimical.com.br",
  description = "Líder brasileira em cal virgem e cal hidratada para indústria.",
  publisher = "Quimical"
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": name,
    "url": url,
    "description": description,
    "publisher": {
      "@type": "Organization",
      "name": publisher
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${url}/produtos?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};
